import api from '@/api';

export type BillingCycle = 'monthly' | 'yearly';
export type BillingCurrency = 'usd' | 'inr';

/**
 * Mirrors `PlanFeatures` in trovepile-firebase-master/functions/src/config/plans.ts
 * (May 2026 normalization). Numeric fields gate counts; boolean fields gate
 * capabilities; `digestEmails` is a string enum.
 * When `PlanFeatures` changes shape, update here and see `docs/PRICING.md` §13.
 */
export type DigestFrequency = 'off' | 'weekly' | 'daily+weekly';

export interface BillingPlanFeatures {
  // -- Resource quotas (numeric) --------------------------------------------
  bookmark: number;
  notes: number;
  users: number;
  highlighter: number;
  sessionbookmarkgroup: number;
  tags: number;
  taskstag: number;
  groups: number;
  tasks: number;
  workspace: number;
  smartCollections: number;
  apiCallsPerMonth: number;
  noteMediaStorage: number;
  maxNoteBodyBytes: number;

  // -- AI quotas ------------------------------------------------------------
  ai_search: number;
  ai_assistant: number;
  ai_suggest: number;
  ai_summarize: number;
  ai_resurface: number;

  // -- Boolean entitlements -------------------------------------------------
  publicShareLinks: boolean;
  collaboration: boolean;
  dataExport: boolean;
  dataImport: boolean;
  byok: boolean;
  aiYoutube: boolean;
  aiBookmarkContext: boolean;
  kanbanViews: boolean;
  prioritySupport: boolean;
  ssoSaml: boolean;
  auditLog: boolean;
  publicApiRead: boolean;
  publicApiWrite: boolean;
  webhooks: boolean;
  e2eWorkspaces: boolean;
  workMemoryAi: boolean;

  // -- Other policy fields --------------------------------------------------
  digestEmails: DigestFrequency;
}

/** Boolean-entitlement key union, used by `<Gate feature=...>` wrapper. */
export type BillingFeatureKey =
  | 'publicShareLinks'
  | 'collaboration'
  | 'dataExport'
  | 'dataImport'
  | 'byok'
  | 'aiYoutube'
  | 'aiBookmarkContext'
  | 'kanbanViews'
  | 'prioritySupport'
  | 'ssoSaml'
  | 'auditLog'
  | 'publicApiRead'
  | 'publicApiWrite'
  | 'webhooks'
  | 'e2eWorkspaces'
  | 'workMemoryAi';

/** Numeric resource keys used by `<QuotaMeter resource=...>`. */
export type BillingResourceKey =
  | 'bookmark'
  | 'notes'
  | 'users'
  | 'highlighter'
  | 'sessionbookmarkgroup'
  | 'tags'
  | 'taskstag'
  | 'groups'
  | 'tasks'
  | 'workspace'
  | 'smartCollections'
  | 'apiCallsPerMonth'
  | 'noteMediaStorage';

/** Monthly AI bucket keys — mirrors {@link BillingPlanFeatures} AI fields. */
export type AiQuotaKey = 'ai_search' | 'ai_assistant' | 'ai_suggest' | 'ai_summarize' | 'ai_resurface';

/**
 * Plan keys after the May 2026 normalization. Server may still return legacy
 * values from older Firestore docs, hence the union also accepts "plus" and
 * the legacy meaning of "pro" (id 2). Always derive UX from {@link BillingPlan.id}.
 */
export type BillingPlanKey = 'free' | 'pro' | 'team' | 'business' | 'plus';

export interface BillingPlan {
  id: number;
  key: BillingPlanKey;
  name: string;
  price: number;
  prices: {
    usdMonthly: number;
    inrMonthly: number;
  };
  recommended?: boolean;
  isContactSales?: boolean;
  features: BillingPlanFeatures;
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'paused'
  | 'pendingCancel';

export interface BillingSubscription {
  aid: string;
  planId: number;
  planKey: BillingPlan['key'];
  /**
   * Trial-aware effective plan. Server adds this to /billing/subscription so
   * client gating reads the right number while in a 14-day Pro trial (Section 5).
   */
  effectivePlanId?: number;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  currency: BillingCurrency;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
  failureReason: string | null;

  // -- Trial fields (Section 5) ----------------------------------------------
  trialStart?: number | null;
  trialEnd?: number | null;
  trialPlanId?: number | null;
  trialKind?: 'standard' | 'pocket_migration' | null;
  trialUsed?: boolean;

  // -- Lifecycle fields (Section 8) ------------------------------------------
  gracePeriodEndsAt?: number | null;
  readOnlyMode?: boolean;
  lastSuccessfulRenewalAt?: number | null;
  retryAttempts?: number;
  vaultProcessedAt?: number | null;

  // -- Save-the-cancel pause (Section 9.1) -----------------------------------
  pauseUntil?: number | null;
  pausesUsed?: { since: number; count: number };

  retentionCouponExpiresAt?: number | null;
  retentionCouponLastGrantedAt?: number | null;
}

export interface PendingDeletion {
  requestedAt: number;
  scheduledAt: number;
  undoUntil: number;
}

export interface BillingInvoice {
  invoiceId: string;
  amount: number;
  currency: BillingCurrency;
  status: 'draft' | 'open' | 'paid' | 'failed' | 'refunded' | 'void';
  createdAt: number;
  hostedInvoiceUrl?: string;
}

export async function getBillingPlans(): Promise<BillingPlan[]> {
  const { data } = await api.get<{ status: string; plans?: BillingPlan[] }>('/billing/plans');
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to load plans');
  return data.plans ?? [];
}

export type GetBillingSubscriptionOptions = {
  /**
   * When true, requests up to 5 recent invoices embedded in the response.
   * Default false avoids an extra Firestore read on hot paths (entitlements, lifecycle banner).
   * Prefer `listBillingInvoices()` for the full invoice list (settings page).
   */
  includeInvoices?: boolean;
};

export async function getBillingSubscription(
  options?: GetBillingSubscriptionOptions
): Promise<{ subscription: BillingSubscription; invoices: BillingInvoice[]; pendingDeletion: PendingDeletion | null }> {
  const { data } = await api.get<{
    status: string;
    subscription: BillingSubscription;
    invoices?: BillingInvoice[];
    pendingDeletion?: PendingDeletion | null;
  }>(
    '/billing/subscription',
    options?.includeInvoices === true ? { params: { includeInvoices: '1' } } : undefined
  );
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to load subscription');
  return {
    subscription: data.subscription,
    invoices: data.invoices ?? [],
    pendingDeletion: data.pendingDeletion ?? null,
  };
}

export async function createCheckoutSession(
  planId: number,
  billingCycle: BillingCycle,
  currency: BillingCurrency
): Promise<{ sessionId: string; checkoutUrl: string; isMockCheckout?: boolean }> {
  const { data } = await api.post<{
    status: string;
    sessionId: string;
    checkoutUrl: string;
    isMockCheckout?: boolean;
  }>('/billing/checkout-session', {
    planId,
    billingCycle,
    currency,
  });
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to create checkout');
  return {
    sessionId: data.sessionId,
    checkoutUrl: data.checkoutUrl,
    isMockCheckout: data.isMockCheckout,
  };
}

export async function completeCheckoutSession(sessionId: string): Promise<void> {
  const { data } = await api.post<{ status: string; message?: string }>(
    '/billing/checkout-session/complete',
    { sessionId }
  );
  if (data.status !== 'success') {
    throw new Error(data.message ?? 'Failed to complete checkout');
  }
}

/**
 * Mock beta checkout: prefer `/billing/checkout-session/complete`, fall back to
 * `/billing/mock/webhook` when the dedicated complete route is unavailable on
 * an older functions deploy.
 */
export async function finalizeMockCheckoutSession(
  sessionId: string,
  aid: string,
  email?: string | null
): Promise<void> {
  try {
    await completeCheckoutSession(sessionId);
    return;
  } catch (completeErr) {
    try {
      await triggerMockBillingWebhook('checkout.session.completed', aid, {
        sessionId,
        email: email ?? '',
      });
    } catch {
      throw completeErr;
    }
  }
}

export async function createPortalSession(): Promise<{ portalUrl: string }> {
  const { data } = await api.post<{ status: string; portalUrl: string }>('/billing/portal-session');
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to open billing portal');
  return { portalUrl: data.portalUrl };
}

export async function listBillingInvoices(): Promise<BillingInvoice[]> {
  const { data } = await api.get<{ status: string; invoices?: BillingInvoice[] }>('/billing/invoices');
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to load invoices');
  return data.invoices ?? [];
}

export async function cancelSubscription(
  immediate = false,
  opts?: {
    cancelSurveyReason?: string;
    retentionOfferAccepted?: boolean;
    retentionOfferShown?: boolean;
  }
): Promise<void> {
  const body: Record<string, unknown> = { immediate };
  if (opts?.cancelSurveyReason !== undefined) body.cancelSurveyReason = opts.cancelSurveyReason;
  if (opts?.retentionOfferAccepted !== undefined) body.retentionOfferAccepted = opts.retentionOfferAccepted;
  if (opts?.retentionOfferShown !== undefined) body.retentionOfferShown = opts.retentionOfferShown;
  const { data } = await api.post<{ status: string }>('/billing/subscription/cancel', body);
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to cancel subscription');
}

export async function pauseSubscription(months: 1 | 2 | 3): Promise<void> {
  const { data } = await api.post<{ status: string }>('/billing/subscription/pause', { months });
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to pause subscription');
}

export async function acceptRetentionOffer(): Promise<void> {
  const { data } = await api.post<{ status: string }>('/billing/subscription/accept-retention-offer', {});
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to apply retention offer');
}

export async function requestRefund(invoiceId: string, reason?: string): Promise<void> {
  const { data } = await api.post<{ status: string }>('/billing/subscription/refund', { invoiceId, reason: reason ?? '' });
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to process refund');
}

export async function triggerMockBillingWebhook(
  eventType:
    | 'checkout.session.completed'
    | 'invoice.paid'
    | 'invoice.payment_failed'
    | 'customer.subscription.updated'
    | 'customer.subscription.deleted'
    | 'charge.refunded'
    | 'charge.disputed',
  aid: string,
  payload: Record<string, unknown>
): Promise<void> {
  const secret = import.meta.env.VITE_MOCK_STRIPE_WEBHOOK_SECRET as string | undefined;
  const { data } = await api.post<{ status: string }>('/billing/mock/webhook', {
    eventType,
    aid,
    data: payload,
  }, {
    headers: secret ? { 'x-mock-stripe-secret': secret } : undefined,
  });
  if (data.status !== 'success') throw new Error((data as { message?: string }).message ?? 'Failed to trigger mock webhook');
}

export async function runMockBillingValidation(): Promise<{
  status: 'success' | 'error';
  message?: string;
  report?: Array<{ step: string; ok: boolean; details?: string }>;
}> {
  const { data } = await api.post<{
    status: 'success' | 'error';
    message?: string;
    report?: Array<{ step: string; ok: boolean; details?: string }>;
  }>('/billing/mock/validate');
  return data;
}
